import React, { useEffect, useRef, useState } from 'react';
import {
  CheckCircle2, XCircle, AlertTriangle,
  LayoutGrid, Layers, Paintbrush, Database, Link2, Bot, Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { FOREST_GREEN, MID_GRAY, ERROR, WARNING } from '@/styles/tokens';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';

export type StepStatus = 'pending' | 'active' | 'complete' | 'error';

export interface PipelineStep {
  name: string;
  status: StepStatus;
  duration_ms?: number;
  actionsCount?: number;
  verification?: {
    passed: boolean;
    issueCount: number;
    summary: string;
  };
}

export interface MultiPageProgress {
  totalPages: number;
  currentPageIndex: number;
  currentPageName: string;
  pages: Array<{ pageId: string; pageName: string; status: 'pending' | 'active' | 'complete' }>;
}

interface StreamingProgressIndicatorProps {
  steps: PipelineStep[];
  statusMessage?: string;
  onCancel?: () => void;
  isActive: boolean;
  multiPage?: MultiPageProgress;
}

/** Map step names to lucide icons (aligned with TracesTab PIPELINE_STEPS). */
const STEP_ICON_MAP: Record<string, LucideIcon> = {
  'Layout': LayoutGrid,
  'Components': Layers,
  'Styles': Paintbrush,
  'Data Discovery': Database,
  'Data Binding': Link2,
  'Agent': Bot,
  'cached': Zap,
};

function getStepIcon(stepName: string): LucideIcon {
  if (STEP_ICON_MAP[stepName]) return STEP_ICON_MAP[stepName];
  const lower = stepName.toLowerCase();
  if (lower.includes('style')) return Paintbrush;
  if (lower.includes('layout')) return LayoutGrid;
  if (lower.includes('component')) return Layers;
  if (lower.includes('data')) return Database;
  return Bot;
}

const STYLE_ID = 'streaming-progress-keyframes';

/** Status-dependent color for the step icon. */
function stepColor(status: StepStatus) {
  switch (status) {
    case 'active': return FOREST_GREEN.DEFAULT;
    case 'complete': return FOREST_GREEN.light;
    case 'error': return ERROR.DEFAULT;
    default: return MID_GRAY.DEFAULT;
  }
}

/**
 * Horizontal icon-only step indicator for the agent pipeline.
 * Shows progress through compact icon chips with status overlays.
 */
export const StreamingProgressIndicator: React.FC<StreamingProgressIndicatorProps> = ({
  steps,
  statusMessage,
  isActive,
  multiPage,
}) => {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isActive) {
      startRef.current = null;
      return;
    }
    startRef.current = Date.now();
    setElapsed(0);
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current!) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [isActive]);

  // Inject pulse keyframes once
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      @keyframes stepPulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(21, 45, 7, 0.35); }
        50% { box-shadow: 0 0 0 4px rgba(21, 45, 7, 0.08); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const formatElapsed = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (steps.length === 0 && !isActive) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '4px 0',
      }}
    >
      {/* Multi-page progress (top level) */}
      {multiPage && multiPage.totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: FOREST_GREEN.DEFAULT }}>
            Page {multiPage.currentPageIndex + 1}/{multiPage.totalPages}
          </span>
          <div style={{ display: 'flex', gap: 3, flex: 1 }}>
            {multiPage.pages.map((page) => (
              <div
                key={page.pageId}
                style={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: page.status === 'complete' ? FOREST_GREEN.light
                    : page.status === 'active' ? FOREST_GREEN.DEFAULT
                    : `${MID_GRAY.DEFAULT}30`,
                  transition: 'background-color 0.3s ease',
                }}
                title={page.pageName}
              />
            ))}
          </div>
          <span style={{ fontSize: 11, color: MID_GRAY.DEFAULT }}>
            {multiPage.currentPageName}
          </span>
        </div>
      )}

      {/* Step indicators — only shown for multi-step pipelines */}
      {steps.length > 1 && (
        <TooltipProvider delayDuration={200}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            {steps.map((step, idx) => {
              const Icon = getStepIcon(step.name);
              const color = stepColor(step.status);

              // Determine bottom-right overlay: verification takes priority
              const overlay = (() => {
                if (step.verification && step.verification.issueCount > 0) {
                  const OverlayIcon = step.verification.passed ? AlertTriangle : XCircle;
                  const overlayColor = step.verification.passed ? WARNING.DEFAULT : ERROR.DEFAULT;
                  return (
                    <OverlayIcon
                      className="w-2.5 h-2.5"
                      style={{
                        position: 'absolute', bottom: -2, right: -2,
                        color: overlayColor,
                        backgroundColor: 'white',
                        borderRadius: '50%',
                      }}
                    />
                  );
                }
                if (step.status === 'complete') {
                  return (
                    <CheckCircle2
                      className="w-2.5 h-2.5"
                      style={{
                        position: 'absolute', bottom: -2, right: -2,
                        color: FOREST_GREEN.light,
                        backgroundColor: 'white',
                        borderRadius: '50%',
                      }}
                    />
                  );
                }
                if (step.status === 'error') {
                  return (
                    <XCircle
                      className="w-2.5 h-2.5"
                      style={{
                        position: 'absolute', bottom: -2, right: -2,
                        color: ERROR.DEFAULT,
                        backgroundColor: 'white',
                        borderRadius: '50%',
                      }}
                    />
                  );
                }
                return null;
              })();

              return (
                <React.Fragment key={step.name}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        role="status"
                        aria-label={step.name}
                        style={{
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          color,
                          backgroundColor: step.status === 'active' ? `${FOREST_GREEN.DEFAULT}12`
                            : step.status === 'error' ? `${ERROR.DEFAULT}12`
                            : 'transparent',
                          animation: step.status === 'active' ? 'stepPulse 2s ease-in-out infinite' : 'none',
                          transition: 'background-color 0.3s ease, color 0.3s ease',
                          cursor: 'default',
                          flexShrink: 0,
                        }}
                      >
                        <Icon className="w-4 h-4" />
                        {overlay}
                        {/* Actions count badge */}
                        {step.status === 'complete' && step.actionsCount !== undefined && step.actionsCount > 0 && (
                          <span
                            style={{
                              position: 'absolute',
                              top: -4,
                              right: -4,
                              minWidth: 14,
                              height: 14,
                              borderRadius: 7,
                              backgroundColor: FOREST_GREEN.DEFAULT,
                              color: 'white',
                              fontSize: 9,
                              fontWeight: 700,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '0 3px',
                              lineHeight: 1,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {step.actionsCount}
                          </span>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      {step.name}
                      {step.verification && step.verification.issueCount > 0 && (
                        <span style={{ color: step.verification.passed ? WARNING.DEFAULT : ERROR.DEFAULT }}>
                          {' '}— {step.verification.summary}
                        </span>
                      )}
                    </TooltipContent>
                  </Tooltip>

                  {/* Line connector */}
                  {idx < steps.length - 1 && (
                    <div style={{
                      width: 20,
                      height: 1,
                      backgroundColor: MID_GRAY.DEFAULT,
                      opacity: 0.3,
                      flexShrink: 0,
                    }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </TooltipProvider>
      )}

      {/* Status message + elapsed timer */}
      {statusMessage && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 12, color: MID_GRAY.DEFAULT, fontStyle: 'italic' }}>
            {statusMessage}
          </span>
          {isActive && (
            <span style={{ fontSize: 11, color: MID_GRAY.DEFAULT, fontVariantNumeric: 'tabular-nums', opacity: 0.7 }}>
              {formatElapsed(elapsed)}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Build the steps array based on orchestration type.
 */
export function buildPipelineSteps(pipelineType: 'template-first' | 'data-first' | 'single-agent', agentType?: string): PipelineStep[] {
  if (pipelineType === 'single-agent') {
    return [{ name: agentType || 'Agent', status: 'pending' }];
  }
  if (pipelineType === 'data-first') {
    return [
      { name: 'Data Discovery', status: 'pending' },
      { name: 'Layout', status: 'pending' },
      { name: 'Components', status: 'pending' },
      { name: 'Data Binding', status: 'pending' },
      { name: 'Styles', status: 'pending' },
    ];
  }
  // template-first
  return [
    { name: 'Layout', status: 'pending' },
    { name: 'Components', status: 'pending' },
    { name: 'Styles', status: 'pending' },
  ];
}
